import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { buyerSettingsRestCrudThunksForBuyer } from '../../thunks/buyer_settings_thunks';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { message } from 'antd';
import NotificationPreferenceCard from './NotificationPreferenceCard';
import NotificationPreferenceSuccessCard from './NotificationPreferenceSuccessCard';
import { getNotificationDetailsFromKey } from '../../utils/NotificationUtils';
import { withUserType } from '../withUserType';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { supplierSettingsRestCrudThunksForSupplier } from '../../thunks/supplier_settings_thunks';

const NotificationPreferencesPage: FC<any> = ({
	match,
	settings,
	updateSettings,
	history,
	userType,
}): React.ReactElement => {
	const [updating, setUpdating] = useState(false);
	const [showSuccessScreen, setShowSuccessScreen] = useState(false);

	const notificationType = match.params.notificationType;

	const notificationDetails = getNotificationDetailsFromKey(userType)(notificationType);

	const existingRootNotificationConfig = useMemo(
		() => nullSafeGetOrElse(notificationDetails.notificationKey, settings, {}),
		[settings, notificationDetails]
	);

	const existingNotificationConfig = useMemo(
		() => nullSafeGet(notificationDetails.key, existingRootNotificationConfig),
		[existingRootNotificationConfig, notificationDetails]
	);

	const onUpdatePreference = useCallback(
		(values) => {
			const newSettings = {
				...settings,
				[notificationDetails.notificationKey]: {
					...existingRootNotificationConfig,
					[notificationDetails.key]: {
						sms: false,
						email: values.email,
						inApp: values.inApp,
						important: nullSafeGetOrElse('important', existingNotificationConfig, false),
					},
				},
			};
			setUpdating(true);
			updateSettings(newSettings)
				.then(() => setShowSuccessScreen(true))
				.catch(message.error)
				.finally(() => setUpdating(false));
		},
		[
			settings,
			existingNotificationConfig,
			notificationDetails,
			existingRootNotificationConfig,
			updateSettings,
		]
	);

	const onChangePreference = () => setShowSuccessScreen(false);

	const onCancel = useCallback(() => {
		history.replace('/');
	}, [history]);

	if (!notificationDetails) {
		return null;
	}

	return showSuccessScreen ? (
		<NotificationPreferenceSuccessCard
			onChangePreference={onChangePreference}
			notificationDetails={notificationDetails}
		/>
	) : (
		<NotificationPreferenceCard
			notificationDetails={notificationDetails}
			onUpdatePreference={onUpdatePreference}
			onCancel={onCancel}
			updating={updating}
			existingConfig={existingNotificationConfig}
		/>
	);
};

const mapStateToProps = (state, ownProps) => ({
	settings:
		ownProps.userType === ROLE_TYPES.SUPPLIER
			? state.supplier_settings.detail
			: state.buyer_settings.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateSettings: (settings) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? supplierSettingsRestCrudThunksForSupplier.update(settings)
				: buyerSettingsRestCrudThunksForBuyer.update(settings)
		),
});

export default withRouter<any, any>(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(NotificationPreferencesPage))
);
